import { OM_MODELS, type OmModelDef, type OmVariableId } from './om-types.js'

export type Bbox = [latMin: number, lonMin: number, latMax: number, lonMax: number]

export function bboxContainsExtent(modelBbox: Bbox, mapExtent: Bbox): boolean {
  const [mLatMin, mLonMin, mLatMax, mLonMax] = modelBbox
  const [eLat0, eLon0, eLat1, eLon1] = mapExtent
  return eLat0 >= mLatMin && eLat1 <= mLatMax && eLon0 >= mLonMin && eLon1 <= mLonMax
}

// True if the model bbox overlaps the viewport at all (not necessarily contains it).
// Used for the SELECTABLE list: a regional model that covers even part of the view
// can render something there, so it's a legitimate manual choice — distinct from
// AUTO-select, which still requires full containment so the whole view is covered.
export function bboxIntersectsExtent(modelBbox: Bbox, mapExtent: Bbox): boolean {
  const [mLatMin, mLonMin, mLatMax, mLonMax] = modelBbox
  const [eLat0, eLon0, eLat1, eLon1] = mapExtent
  return eLat0 <= mLatMax && eLat1 >= mLatMin && eLon0 <= mLonMax && eLon1 >= mLonMin
}

export function selectBestModel(
  mapExtent: Bbox,
  variable: OmVariableId,
  preferredGlobalId?: string
): OmModelDef | null {
  const candidates = OM_MODELS.filter(m => {
    if (!m.variables.has(variable)) return false
    if (!m.bbox) return true
    return bboxContainsExtent(m.bbox, mapExtent)
  })

  if (candidates.length === 0) return null

  if (preferredGlobalId) {
    const preferred = candidates.find(m => m.id === preferredGlobalId)
    if (preferred && !preferred.bbox) return preferred
  }

  candidates.sort((a, b) => b.priority - a.priority)
  return candidates[0]
}

export function getModelsForVariable(variable: OmVariableId): OmModelDef[] {
  return OM_MODELS.filter(m => m.variables.has(variable))
}

// Models the user may pin for the given viewport: every global model, plus
// regional models whose bbox INTERSECTS the view (covers at least part of it), so
// anything that could render something is offered. Ordered best-first by priority.
// (Auto-select is stricter — it requires full containment via selectBestModel.)
export function getCandidateModels(variable: OmVariableId, mapExtent: Bbox): OmModelDef[] {
  return OM_MODELS.filter(m => {
    if (!m.variables.has(variable)) return false
    if (!m.bbox) return true
    return bboxIntersectsExtent(m.bbox, mapExtent)
  }).sort((a, b) => b.priority - a.priority)
}

export function getGlobalModelsForVariable(variable: OmVariableId): OmModelDef[] {
  return OM_MODELS.filter(m => m.variables.has(variable) && !m.bbox)
}

export function getRegionalModelsForVariable(variable: OmVariableId): OmModelDef[] {
  return OM_MODELS.filter(m => m.variables.has(variable) && m.bbox !== null)
}
